import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { CircularProgress } from "@mui/material";
import Notiflix from "notiflix";
import { getCombos } from "../../../../../data/api/combos/combos";
import useIndex from "../../../../../hooks/useIndex";
import useWompiCheckout from "../../../../../hooks/useWompiCheckout";
import validErrors from "../../../../../hooks/validErrors";
import Logo from "../../../../../assets/img/logo.png";

const formatMoney = (value) =>
  String(Math.round(Number(value ?? 0))).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const CONFETTI_COLORS = [
  "#ffd400",
  "#ffe066",
  "#fff2a8",
  "#ffb000",
  "#e63946",
  "#b3001b",
  "#ff5252",
  "#0d3b9c",
  "#1c5cd1",
  "#42a5f5",
  "#2ed573",
  "#ff9eb1",
  "#ffffff",
];

const CONFETTI_SHAPES = ["rect", "square", "circle", "ribbon"];

const CombosList = ({ onSelect, onClose }) => {
  const { token } = useIndex();
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState(null);

  const { openCheckout, loading: wompiLoading } = useWompiCheckout({
    closeParentModal: () => onClose?.(),
  });

  const sparkles = useMemo(
    () =>
      Array.from({ length: 22 }).map((_, i) => ({
        id: i,
        top: Math.random() * 96,
        left: Math.random() * 96,
        delay: Math.random() * 4,
        duration: 2.4 + Math.random() * 2.6,
        scale: 0.6 + Math.random() * 0.8,
      })),
    []
  );

  const confetti = useMemo(
    () =>
      Array.from({ length: 130 }).map((_, i) => {
        const shape = CONFETTI_SHAPES[i % CONFETTI_SHAPES.length];
        const baseSize = 4 + Math.random() * 6;
        let width = baseSize;
        let height = baseSize;
        if (shape === "rect") {
          width = 3 + Math.random() * 4;
          height = 9 + Math.random() * 14;
        } else if (shape === "ribbon") {
          width = 2 + Math.random() * 3;
          height = 14 + Math.random() * 18;
        }
        return {
          id: i,
          shape,
          top: Math.random() * 100,
          left: Math.random() * 100,
          rotate: Math.random() * 360,
          width,
          height,
          color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
          delay: Math.random() * 5,
          duration: 3.5 + Math.random() * 5,
          drift: Math.random() < 0.35,
        };
      }),
    []
  );

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const { data } = await getCombos(token, { status: 1, per_page: 100 });
        if (!mounted) return;
        setCombos(data?.success ? (data?.data ?? []) : []);
      } catch (error) {
        validErrors(error);
        if (mounted) setCombos([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleComboClick = async (combo) => {
    if (!token) {
      Notiflix.Notify.warning("Debes iniciar sesión para comprar un combo.");
      return;
    }

    // Defensa: aunque la API filtra por status=1, validamos en cliente para
    // evitar cobrar un combo que pudo desactivarse mientras el modal estaba
    // abierto (ej. admin lo desactivó en otra pestaña).
    if (Number(combo?.status) !== 1) {
      Notiflix.Notify.warning("Este combo no está disponible en este momento.");
      return;
    }

    const valuePesos = parseInt(combo?.value, 10);
    if (!Number.isFinite(valuePesos) || valuePesos <= 0) {
      Notiflix.Notify.failure("El combo no tiene un valor válido.");
      return;
    }

    onSelect?.(combo);
    setPurchasingId(combo.id);
    try {
      await openCheckout({
        description: `Compra Fenabocol — ${combo.name}`,
        currency: "COP",
        invoice_origin: "Bingo",
        lines: [
          {
            description: `${combo.name}: ${combo.moves} jugada${Number(combo.moves) === 1 ? "" : "s"}`,
            product_name: combo.name,
            amount: valuePesos * 100,
          },
        ],
        transaction_data: { combo_id: combo.id },
      });
    } finally {
      setPurchasingId(null);
    }
  };

  return (
    <Wrap>
      <Rays aria-hidden="true" />
      <RaysSoft aria-hidden="true" />
      <RedSplashTop aria-hidden="true" />
      <RedSplashBottom aria-hidden="true" />

      <Confetti aria-hidden="true">
        {confetti.map((p) => (
          <i
            key={p.id}
            data-shape={p.shape}
            data-anim={p.drift ? "drift" : "sway"}
            style={{
              top: `${p.top}%`,
              left: `${p.left}%`,
              width: `${p.width}px`,
              height: `${p.height}px`,
              background: p.color,
              "--rot": `${p.rotate}deg`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        ))}
      </Confetti>

      <Sparkles aria-hidden="true">
        {sparkles.map((s) => (
          <span
            key={s.id}
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
              transform: `scale(${s.scale})`,
            }}
          />
        ))}
      </Sparkles>

      <Content>
        <Header>
          <img src={Logo} alt="logo" />
          <Title>
            <span>BINGO</span>
            <span className="accent">FENABOCOL</span>
          </Title>
        </Header>

        <ScrollArea>
          {loading ? (
            <CenterBox>
              <Loader>
                <SpinnerRing>
                  <CircularProgress
                    size={56}
                    thickness={4.5}
                    sx={{ color: "#fff8d2" }}
                  />
                </SpinnerRing>
                <LoaderText>Cargando combos…</LoaderText>
              </Loader>
            </CenterBox>
          ) : combos.length === 0 ? (
            <CenterBox>
              <Empty>No hay combos disponibles</Empty>
            </CenterBox>
          ) : (
            <Grid>
              {combos.map((c) => {
                const busy = wompiLoading && purchasingId === c.id;
                return (
                  <ComboCard
                    key={c.id}
                    type="button"
                    onClick={() => handleComboClick(c)}
                    disabled={wompiLoading}
                    aria-busy={busy}
                  >
                    {busy ? (
                      <CardSpinner>
                        <CircularProgress size={28} sx={{ color: "#ffe27a" }} />
                      </CardSpinner>
                    ) : (
                      <>
                        <CardName>{c.name}</CardName>
                        <CardValue>
                          Donación <b>${formatMoney(c.value)}</b>
                        </CardValue>
                        <CardMoves>
                          x {c.moves} {Number(c.moves) === 1 ? "jugada" : "jugadas"}
                        </CardMoves>
                      </>
                    )}
                  </ComboCard>
                );
              })}
            </Grid>
          )}
        </ScrollArea>
      </Content>
    </Wrap>
  );
};

export default CombosList;

/* ============================================================
   ANIMATIONS
============================================================ */

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(.6); }
  50%      { opacity: 1; transform: scale(1.1); }
`;

const rotateRays = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
`;

const rotateRaysReverse = keyframes`
  from { transform: translate(-50%, -50%) rotate(360deg); }
  to   { transform: translate(-50%, -50%) rotate(0deg); }
`;

const confettiSway = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(var(--rot, 0deg)); }
  50%      { transform: translate(4px, -8px) rotate(calc(var(--rot, 0deg) + 18deg)); }
`;

const confettiDrift = keyframes`
  0%   { transform: translate(-6px, -10px) rotate(var(--rot, 0deg)); opacity: .9; }
  50%  { transform: translate(8px, 12px) rotate(calc(var(--rot, 0deg) + 60deg)); opacity: 1; }
  100% { transform: translate(-4px, 22px) rotate(calc(var(--rot, 0deg) + 120deg)); opacity: .85; }
`;

/* ============================================================
   STYLES
============================================================ */

const Wrap = styled.div`
  --gold: #f3c54a;
  --gold-soft: #ffe27a;
  --gold-deep: #b8862a;
  --red-1: #a31616;
  --red-2: #720a0a;
  --red-3: #3a0303;

  position: relative;
  width: min(820px, 95vw);
  max-height: 92vh;
  overflow: hidden;
  border-radius: 22px;
  color: #fff;
  isolation: isolate;
  display: flex;
  flex-direction: column;

  /* Fondo dorado con destellos cálidos */
  background:
    radial-gradient(circle at 50% 50%, #ffe066 0%, #f4b400 40%, #c98700 80%, #8a4f00 100%);
  box-shadow:
    0 30px 80px rgba(0, 0, 0, 0.55),
    0 0 0 3px #b8862a inset,
    0 0 0 6px rgba(255, 230, 130, 0.5) inset,
    0 0 0 8px #8a4f00 inset;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 22px 22px 24px;
`;

const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
  margin-right: -6px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(180, 50, 50, 0.55);
    border-radius: 8px;
  }
`;

const Rays = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 240%;
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  opacity: 0.55;
  background: repeating-conic-gradient(
    from 0deg,
    rgba(255, 255, 200, 0.65) 0deg 4deg,
    rgba(255, 196, 60, 0.0) 4deg 12deg
  );
  mask-image: radial-gradient(circle, #000 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(circle, #000 30%, transparent 75%);
  animation: ${rotateRays} 90s linear infinite;
`;

const RaysSoft = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 220%;
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
  background: repeating-conic-gradient(
    from 6deg,
    rgba(255, 255, 255, 0.4) 0deg 2deg,
    rgba(255, 196, 60, 0.0) 2deg 14deg
  );
  mask-image: radial-gradient(circle, #000 25%, transparent 75%);
  -webkit-mask-image: radial-gradient(circle, #000 25%, transparent 75%);
  filter: blur(2px);
  animation: ${rotateRaysReverse} 140s linear infinite;
`;

const RedSplashTop = styled.div`
  position: absolute;
  inset: -2px -2px auto -2px;
  height: 18%;
  pointer-events: none;
  z-index: 0;
  background:
    linear-gradient(180deg, rgba(180, 0, 30, 0.85), rgba(180, 0, 30, 0));
  clip-path: polygon(
    0 0,
    100% 0,
    100% 60%,
    78% 100%,
    62% 50%,
    45% 100%,
    25% 55%,
    0 100%
  );
`;

const RedSplashBottom = styled.div`
  position: absolute;
  inset: auto -2px -2px -2px;
  height: 22%;
  pointer-events: none;
  z-index: 0;
  background:
    linear-gradient(0deg, rgba(180, 0, 30, 0.92), rgba(180, 0, 30, 0));
  clip-path: polygon(
    0 100%,
    100% 100%,
    100% 50%,
    82% 90%,
    65% 40%,
    50% 95%,
    35% 45%,
    18% 90%,
    0 50%
  );
`;

const Confetti = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 1;

  i {
    position: absolute;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.28);
    --rot: 0deg;
    transform: rotate(var(--rot));
  }

  i[data-shape="rect"] {
    border-radius: 1px;
  }
  i[data-shape="square"] {
    border-radius: 2px;
  }
  i[data-shape="circle"] {
    border-radius: 50%;
  }
  i[data-shape="ribbon"] {
    border-radius: 6px;
    opacity: 0.92;
  }

  i[data-anim="sway"] {
    animation: ${confettiSway} ease-in-out infinite;
  }
  i[data-anim="drift"] {
    animation: ${confettiDrift} ease-in-out infinite;
  }
`;

const Sparkles = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 1;

  span {
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: radial-gradient(circle, #fff7c2 0%, #ffd95e 60%, transparent 70%);
    box-shadow: 0 0 10px rgba(255, 255, 200, 0.85);
    animation: ${sparkle} 3s ease-in-out infinite;
  }
`;

const Header = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;

  img {
    width: clamp(72px, 12vw, 96px);
    height: auto;
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.45));
  }
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: clamp(1.7rem, 5vw, 2.6rem);
  color: #fff8d2;
  -webkit-text-stroke: 1.5px #7a1414;
  text-shadow:
    0 2px 0 #7a1414,
    0 4px 0 rgba(0, 0, 0, 0.35),
    0 0 14px rgba(255, 196, 76, 0.6);

  .accent {
    color: #fff;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(10px, 2vw, 16px);

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const ComboCard = styled.button`
  position: relative;
  border: 0;
  cursor: pointer;
  padding: 16px 12px;
  border-radius: 14px;
  text-align: center;
  color: #fff;

  background:
    radial-gradient(circle at 50% 0%, rgba(255, 215, 100, 0.18), transparent 55%),
    linear-gradient(160deg, var(--red-1) 0%, var(--red-2) 55%, var(--red-3) 100%);

  box-shadow:
    inset 0 0 0 2px var(--gold),
    inset 0 0 0 4px rgba(0, 0, 0, 0.35),
    inset 0 0 0 6px var(--gold-deep),
    0 8px 18px rgba(0, 0, 0, 0.5),
    0 0 14px rgba(255, 196, 76, 0.18);

  transition: transform 0.14s ease, filter 0.14s ease, box-shadow 0.14s ease;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, #fff5b1 0%, var(--gold) 55%, var(--gold-deep) 100%);
    box-shadow: 0 0 6px rgba(255, 215, 100, 0.7);
  }
  &::before {
    top: 9px;
    left: 9px;
  }
  &::after {
    bottom: 9px;
    right: 9px;
  }

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.08);
    box-shadow:
      inset 0 0 0 2px var(--gold-soft),
      inset 0 0 0 4px rgba(0, 0, 0, 0.35),
      inset 0 0 0 6px var(--gold),
      0 12px 22px rgba(0, 0, 0, 0.55),
      0 0 18px rgba(255, 215, 100, 0.45);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
    filter: brightness(0.85) saturate(0.85);
    transform: none;
  }
`;

const CardName = styled.div`
  font-weight: 900;
  letter-spacing: 0.08em;
  color: var(--gold-soft);
  font-size: clamp(14px, 2.8vw, 17px);
  text-transform: uppercase;
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.6);
  margin-bottom: 6px;
`;

const CardValue = styled.div`
  font-weight: 700;
  font-size: clamp(12px, 2.6vw, 14px);
  line-height: 1.15;
  color: #fff;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.5);

  b {
    font-weight: 900;
  }
`;

const CardMoves = styled.div`
  font-weight: 800;
  font-size: clamp(12px, 2.5vw, 14px);
  margin-top: 4px;
  color: #fff;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.5);
`;

const CenterBox = styled.div`
  display: grid;
  place-items: center;
  padding: 32px 0;
`;

const Empty = styled.div`
  color: #7a1414;
  font-weight: 900;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.45);
`;

const CardSpinner = styled.div`
  display: grid;
  place-items: center;
  min-height: 64px;
`;

const loaderPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 6px rgba(255, 248, 210, 0.18), 0 0 22px rgba(255, 248, 210, 0.4); }
  50%      { box-shadow: 0 0 0 10px rgba(255, 248, 210, 0.08), 0 0 32px rgba(255, 248, 210, 0.6); }
`;

const Loader = styled.div`
  display: grid;
  place-items: center;
  gap: 14px;
  padding: 18px 22px;
`;

const SpinnerRing = styled.div`
  display: grid;
  place-items: center;
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 248, 210, 0.15) 0%, rgba(255, 248, 210, 0) 70%);
  animation: ${loaderPulse} 1.6s ease-in-out infinite;
`;

const LoaderText = styled.div`
  font-weight: 900;
  font-size: clamp(14px, 2.6vw, 16px);
  color: #fff8d2;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-shadow:
    0 2px 0 #7a1414,
    0 0 10px rgba(255, 196, 76, 0.4);
`;
